<template>
  <div class="organism-node-management_container">
    <div class="organism-node-management_card">
      <div class="node-management_card__header">
        <span class="card__header--topic">Node List</span>
        <b-dropdown
          v-if="checkedNodeList.length > 0 && hasUpdatePermission"
          no-caret
          right
          v-ripple.400="'rgba(30, 30, 30, 0.15)'"
          text="Dark"
          variant="flat-dark"
          size="sm"
          class="node__dropdown"
        >
          <template #button-content>
            <feather-icon icon="MoreVerticalIcon"></feather-icon>
          </template>
          <b-dropdown-item @click="clickEditTagFromTripleDot"
            ><feather-icon icon="TagIcon" class="mr-1"></feather-icon>
            <span>Edit Tag</span>
          </b-dropdown-item>
          <b-dropdown-item @click="clickEditSNMPFromTripleDot"
            ><feather-icon icon="SlidersIcon" class="mr-1"></feather-icon>
            <span>Edit SNMP Community</span>
          </b-dropdown-item>
          <b-dropdown-item @click="clickEditSSHFromTripleDot"
            ><feather-icon icon="SlidersIcon" class="mr-1"></feather-icon>
            <span>Edit SSH Credential</span>
          </b-dropdown-item>
          <b-dropdown-divider />
          <b-dropdown-item @click="clickDeleteNodeFromTripleDot"
            ><feather-icon icon="Trash2Icon" class="mr-1"></feather-icon>
            <span>Delete Node</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <molecule-node-list
        :nodeList="nodeList"
        :tagsList="tagsList"
        @checkedNodeList="updateCheckedNodeList"
        @selectedNode="updateSelectedNode"
      ></molecule-node-list>
    </div>

    <div class="selected-node-detail__container">
      <div v-if="selectedNodeDetail">
        <div name="node-information__wrapper">
          <molecule-node-information
            :nodeData="selectedNodeDetail"
            :tagOptions="tagsList"
            :canUpdate="hasUpdatePermission"
            @onUpdateCurrentNodeTagList="onUpdateCurrentNodeTagList"
          ></molecule-node-information>
        </div>
        <hr />
        <div name="config-trend__wrapper" class="mb-3 mt-2">
          <molecule-config-change-trend
            :configChangeTrendDataSet="configChangeTrendDataSet"
            @onUpdateSelectedDuration="onUpdateSelectedDuration"
          ></molecule-config-change-trend>
        </div>
        <hr />
        <div name="config-history__wrapper">
          <molecule-config-history
            :configHistoryList="configHistoryList"
            :configHistoryCurrentPage="configHistoryCurrentPage"
            :totalConfigHistory="totalConfigHistory"
            :selectedConfigData="selectedConfigData"
            @onClickPagination="onClickPagination"
            @onClickCompareConfiguration="onClickCompareConfiguration"
            @onSelectedConfigData="onSelectedConfigData"
          ></molecule-config-history>
        </div>
      </div>
      <div
        v-else
        class="node-info_container-no-data d-flex w-100 flex-column align-items-center justify-content-center"
      >
        <feather-icon
          icon="CloudOffIcon"
          class="empty-data__icon"
        ></feather-icon>
        <label class="empty-data__text--title">No Data</label>
      </div>
    </div>

    <!-- ? Modal Edit Delete Nodes Start -->
    <molecule-modal-dialog
      modalId="modal-delete-node"
      :isTextCenter="true"
      icon="AlertCircleIcon"
      textTitle="Do you want to delete?"
      textSubtitle="You won’t be able to revert this!"
      buttonTextLeft="Delete"
      buttonColorLeft="danger"
      :isDisableButtonLeft="isDisabledButtonConfirmDelete"
      :isSlotBody="true"
      @clickButtonLeft="clickConfirmDeleteNodeFromModal"
    >
      <template #body>
        <div class="modal-delete-node__list">
          <ul class="list__ul">
            <li
              v-for="(node, index) in checkedNodeList"
              :key="index"
              class="list__li"
            >
              <label class="list__label">{{ node.hostname }}</label>
            </li>
          </ul>
        </div>
        <div class="modal-delete-node_input">
          <atom-input
            placeholder="delete"
            title="Please type delete to confirm"
            v-model="textConfirmDeleteNode"
          ></atom-input>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- ? Modal Edit Delete Nodes End -->

    <!-- ? Modal Edit Tags Start -->
    <molecule-modal-dialog
      modalId="modal-edit-tag"
      :isTextCenter="true"
      textTitle="Edit Tag"
      textSubtitle="This will apply tags to selected node."
      buttonTextLeft="Apply"
      :isDisableButtonLeft="isDisabledButtonApplyEditTag"
      :isSlotBody="true"
      @clickButtonLeft="clickConfirmApplyEditTagFromModal"
    >
      <template #body>
        <div class="modal-edit-tag__dropdown">
          <div class="dropdown__label--topic">
            <span>Tags</span>
          </div>
          <atom-multi-select
            v-model="selectedTagList"
            :option="tagsList"
            :isPushTaggable="true"
            :isDisable="false"
            :isAppendToBody="false"
            :isEnableProper="false"
            placeholder="Select Tag"
          ></atom-multi-select>
        </div>
        <div class="overwrite__container mt-1">
          <atom-checkbox
            v-model="isOverWrite"
            title="Overwrite tags"
            textStyle="font-size:12px !important;"
          ></atom-checkbox>
        </div>
        <div class="modal-edit-tag__list mt-1">
          <label class="list__label--topic"
            >{{ isOverWrite ? "Overwrite" : "Apply" }} tags to node list
            below</label
          >
          <ul class="list__ul margin-bottom-0">
            <li
              v-for="(node, index) in checkedNodeList"
              :key="index"
              class="list__li"
            >
              <label class="list__label">{{ node.hostname }}</label>
            </li>
          </ul>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- ? Modal Edit Tags End -->

    <!-- ? Modal Edit SNMP Start -->
    <molecule-modal-dialog
      modalId="modal-edit-snmp"
      textTitle="Edit SNMP Community"
      textSubtitle="This will apply SNMP Community to selected node."
      buttonTextLeft="Apply"
      :isTextCenter="true"
      :isDisableButtonLeft="isDisabledButtonConfirmEditSnmp"
      :isSlotBody="true"
      @clickButtonLeft="clickConfirmEditSnmpFromModal"
    >
      <template #body>
        <div class="modal-edit-tag__dropdown">
          <div class="dropdown__label--topic">
            <span>SNMP Community</span>
          </div>
          <atom-select
            v-model="selectedSnmpCommunity"
            label="credentialName"
            :option="snmpList"
            :isAppendToBody="false"
            :isEnableProper="false"
            placeholder="Select SNMP Community"
          ></atom-select>
        </div>
        <div class="modal-edit-tag__list mt-1">
          <label class="list__label--topic"
            >Overwrite SNMP community to node list below</label
          >
          <ul class="list__ul margin-bottom-0">
            <li
              v-for="(node, index) in checkedNodeList"
              :key="index"
              class="list__li"
            >
              <label class="list__label">{{ node.hostname }}</label>
            </li>
          </ul>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- ? Modal Edit SNMP End -->

    <!-- ? Modal Edit SSH Start -->
    <molecule-modal-dialog
      modalId="modal-edit-ssh"
      textTitle="Edit SSH Credential"
      textSubtitle="This will apply SSH Credential to selected node."
      buttonTextLeft="Apply"
      :isTextCenter="true"
      :isDisableButtonLeft="isDisabledButtonConfirmEditSsh"
      :isSlotBody="true"
      @clickButtonLeft="clickConfirmEditSshFromModal"
    >
      <template #body>
        <div class="modal-edit-tag__dropdown">
          <div class="dropdown__label--topic">
            <span>SSH Credential</span>
          </div>
          <atom-select
            v-model="selectedSshCredential"
            label="credentialName"
            :option="sshList"
            :isAppendToBody="false"
            :isEnableProper="false"
            placeholder="Select SSH Credential"
          ></atom-select>
        </div>
        <div class="modal-edit-tag__list mt-1">
          <label class="list__label--topic"
            >Overwrite SSH Credential to node list below</label
          >
          <ul class="list__ul margin-bottom-0">
            <li
              v-for="(node, index) in checkedNodeList"
              :key="index"
              class="list__li"
            >
              <label class="list__label">{{ node.hostname }}</label>
            </li>
          </ul>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- ? Modal Edit SSH End -->
  </div>
</template>

<script>
import { BCard } from "bootstrap-vue"
import { BDropdown, BDropdownItem, BDropdownDivider } from "bootstrap-vue"
import Ripple from "vue-ripple-directive"

import AtomCheckbox from "@/components/atoms/AtomCheckbox.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import AtomMultiSelect from "@/components/atoms/AtomMultiSelect.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import MoleculeNodeInformation from "@/components/molecules/networkConfigurationManagement/MoleculeNodeInformation.vue"
import MoleculeConfigChangeTrend from "@/components/molecules/networkConfigurationManagement/MoleculeConfigChangeTrend.vue"
import MoleculeConfigHistory from "@/components/molecules/networkConfigurationManagement/MoleculeConfigHistory.vue"
import MoleculeNodeList from "@/components/molecules/networkConfigurationManagement/MoleculeNodeList.vue"
import OrganismPolicy from "@/components/organisms/OrganismPolicy.vue"
import OrganismServerInformation from "@/components/organisms/OrganismServerInformation.vue"
import OrganismServerPolicy from "@/components/organisms/OrganismServerPolicy.vue"
import OrganismSystemResource from "@/components/organisms/OrganismSystemResource"
import { PermissionUser } from "@/constants/permission-user"
import mixins from '@/mixins'
import mixinsUser from "@/mixins/mixinsUser"

export default {
  name: "OrganismNodeManagement",
  components: {
    AtomCheckbox,
    AtomInput,
    AtomSelect,
    AtomMultiSelect,
    BCard,
    BDropdown,
    BDropdownDivider,
    BDropdownItem,
    MoleculeConfigChangeTrend,
    MoleculeConfigHistory,
    MoleculeModalDialog,
    MoleculeNodeInformation,
    MoleculeNodeList,
    OrganismPolicy,
    OrganismServerInformation,
    OrganismServerPolicy,
    OrganismSystemResource,
  },
  directives: {
    Ripple,
  },
  mixins: [mixins, mixinsUser],
  props: {
    nodeList: {
      type: Array,
      required: false,
      default: () => [],
    },
    tagsList: {
      type: Array,
      required: true,
    },
    snmpList: {
      type: Array,
      required: false,
      default: () => [],
    },
    sshList: {
      type: Array,
      required: false,
      default: () => [],
    },
    selectedNodeDetail: {
      type: [Object, null],
      required: false,
      default: null,
    },
    configChangeTrendDataSet: {
      type: Array,
      required: false,
      default: () => [],
    },
    configHistoryList: {
      type: Array,
      required: false,
      default: () => [],
    },
    configHistoryCurrentPage: {
      type: Number,
      required: false,
      default: 0,
    },
    totalConfigHistory: {
      type: Number,
      required: false,
      default: 0,
    },
    selectedConfigData: {
      type: String,
      required: false,
      default: "",
    },
  },
  emit: [
    "onSelectedNode",
    "datetimeParams",
    "onUpdateCurrentNodeTagList",
    "onUpdateTagsFromTripleDot",
    "onDeleteNodeFromTripleDot",
    "onUpdateSnmpFromTripleDot",
    "onUpdateSshFromTripleDot",
  ],
  data() {
    return {
      selectedNode: null,
      selectedSnmpCommunity: null,
      selectedSshCredential: null,
      checkedNodeList: [],
      isOverWrite: false,
      textConfirmDeleteNode: "",
      modalId: "",
      selectedTagList: [],
    }
  },
  computed: {
    hasUpdatePermission() {
      return this.hasPermission(PermissionUser.NCM.NODE_MANAGEMENT_WRITE)
    },

    isDisabledButtonConfirmDelete() {
      return this.textConfirmDeleteNode.toLowerCase() !== "delete"
    },
    isDisabledButtonApplyEditTag() {
      return this.selectedTagList.length === 0
    },
    isDisabledButtonConfirmEditSnmp() {
      return this.selectedSnmpCommunity === null
    },
    isDisabledButtonConfirmEditSsh() {
      return this.selectedSshCredential === null
    },
  },
  methods: {
    updateCheckedNodeList(checkedList) {
      this.checkedNodeList = checkedList
    },

    updateSelectedNode(selectedNode) {
      this.selectedNode = selectedNode
      this.$emit("onSelectedNode", selectedNode.nodeId)
    },

    // delete node (multiple nodes)
    clickDeleteNodeFromTripleDot() {
      this.modalId = "modal-delete-node"
      this.$bvModal.show(this.modalId)
    },
    clickConfirmDeleteNodeFromModal() {
      const deleteNodeRequest = {
        nodeIds: this.checkedNodeList.map((equipment) => equipment.nodeId),
      }
      this.$emit("onDeleteNodeFromTripleDot", deleteNodeRequest)
      this.closeModal()
      this.textConfirmDeleteNode = ""
    },

    // edit tag (multiple nodes)
    clickEditTagFromTripleDot() {
      this.modalId = "modal-edit-tag"
      this.$bvModal.show(this.modalId)
    },
    clickConfirmApplyEditTagFromModal() {
      const editTagRequest = {
        nodeIds: this.checkedNodeList.map((equipment) => equipment.nodeId),
        tags: this.selectedTagList,
        overwrite: this.isOverWrite,
      }
      this.$emit("onUpdateTagsFromTripleDot", editTagRequest)
      this.closeModal()
    },

    // edit snmp credential (multiple nodes)
    clickEditSNMPFromTripleDot() {
      this.modalId = "modal-edit-snmp"
      this.$bvModal.show(this.modalId)
    },
    clickConfirmEditSnmpFromModal() {
      const editSnmpRequest = {
        nodeIds: this.checkedNodeList.map((equipment) => equipment.nodeId),
        credential: this.selectedSnmpCommunity.credentialId,
      }
      this.$emit("onUpdateSnmpFromTripleDot", editSnmpRequest)
      this.closeModal()
    },

    // edit ssh credential (multiple nodes)
    clickEditSSHFromTripleDot() {
      this.modalId = "modal-edit-ssh"
      this.$bvModal.show(this.modalId)
    },
    clickConfirmEditSshFromModal() {
      const editSshRequest = {
        nodeIds: this.checkedNodeList.map((equipment) => equipment.nodeId),
        credential: this.selectedSshCredential.credentialId,
      }
      this.$emit("onUpdateSshFromTripleDot", editSshRequest)
      this.closeModal()
    },

    // close modal
    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = ""
    },

    datetimeParams(datetimeParams) {
      this.$emit("datetimeParams", datetimeParams)
    },

    onUpdateCurrentNodeTagList(tagList) {
      this.$emit("onUpdateCurrentNodeTagList", tagList)
    },

    onUpdateSelectedDuration(selectedDuration) {
      this.$emit("onUpdateSelectedDuration", selectedDuration)
    },

    onClickPagination(currentPage) {
      this.$emit("onClickPagination", currentPage)
    },

    onClickCompareConfiguration(selectedNodeId, selectedConfigVersion) {
      this.$emit(
        "onClickCompareConfiguration",
        selectedNodeId,
        selectedConfigVersion
      )
    },

    onSelectedConfigData(nodeId, version) {
      this.$emit("onSelectedConfigData", nodeId, version)
    },
  },
}
</script>

<style lang="scss" scoped>
hr {
  margin-top: 1rem;
  margin-bottom: 1rem;
  border: 0;
  border-top: 1px solid #ebe9f1;
}

.organism-node-management_container {
  display: block;
}

.organism-node-management_card {
  z-index: 1;
  width: 100%;
  height: 100%;
  background: white;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  margin-bottom: 20px;
  padding-bottom: 21px;
}

.node-management_card__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0px 0 15px;
  margin-bottom: 15px;
  height: 54px;
}

.card__header--topic {
  font-weight: 700;
  font-size: 12px;
}

.server-management_card__edit-policy {
  padding: 24px 15px 0 15px;
}

.selected-node-detail__container {
  background-color: #ffffff;
  width: 100%;
}

.edit-policy__list,
.modal-delete-node__list,
.modal-edit-tag_list {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
}

.modal-delete-node__list {
  width: 224.35px;
}

.modal-edit-tag__list,
.overwrite__container {
  width: 100%;
}

.list__ul {
  margin-bottom: 15px;
  padding-left: 16px;
  max-height: 122px;
  overflow-y: auto;
}

.list__ul--full {
  margin-bottom: 15px;
  padding-left: 16px;
  height: 250px;
  overflow-y: auto;
  margin-top: 15px;
}

.list__li {
  color: #babfc7;
}

.list__label {
  color: #6e6b7b;
  font-weight: 400;
  font-size: 14px;
  margin-bottom: 6px;
}

.list__label--title {
  color: #5e5873;
  font-weight: 700;
  font-size: 12px;
}

.list__label--topic {
  color: #6e6b7b;
  font-weight: 400;
  font-size: 12px;
  margin-bottom: 5px;
}

.modal-delete-node_input {
  width: 100%;
}

.modal-edit-tag__dropdown {
  width: 100%;
}

.node__dropdown {
  margin-left: 15px;
}

.dropdown__label--topic {
  color: #6e6b7b;
  font-size: 12px;
  font-weight: 400;
  margin-bottom: 5px;
}

.margin-bottom-0 {
  margin-bottom: 0 !important;
}

.node-info_container-no-data {
  height: calc(100vh - 13.5rem);
}

.empty-data__icon {
  width: 100px;
  height: 100px;
}

.empty-data__text--title {
  color: #5e5873;
  font-weight: 500;
  font-size: 18px;
  margin-top: 20px;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
  .list__ul--full {
    height: 100%;
  }

  .modal-delete-node_input {
    max-width: 324px;
  }

  .modal-edit-tag__dropdown,
  .modal-edit-tag__list,
  .overwrite__container {
    width: 395px;
  }
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
  .organism-node-management_container {
    display: flex;
  }
  .organism-node-management_card {
    width: 286px;
    height: calc(100vh - 13.1rem);
    border-radius: 6px 0px 0px 6px;
  }
  .selected-node-detail__container {
    height: calc(100vh - 13.1rem);
    overflow-y: auto;
  }
}

// X-Large devices (large desktops, 1200px and up)
@media screen and (min-width: 1200px) {
}
</style>
